import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { retryWithBackoff } from '@/utils/retry';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Handle PASSWORD_RECOVERY event if needed:
      if (event === 'PASSWORD_RECOVERY') {
        // You might store the token in sessionStorage if needed.
        // For example: sessionStorage.setItem('resetToken', token);
        // But note: we’re not calling navigate here.
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await retryWithBackoff(() =>
      supabase.auth.signInWithPassword({ email, password })
    );
    if (error) throw error;
    try {
      analytics.capture('user_signed_in', { email });
    } catch (error) {
      console.warn('Failed to track sign-in event:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await retryWithBackoff(() =>
      supabase.auth.signUp({ email, password })
    );
    if (error) throw error;
    try {
      analytics.capture('user_signed_up', { email });
    } catch (error) {
      console.warn('Failed to track sign-up event:', error);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    setUser(null);
    try {
      analytics.capture('user_signed_out');
    } catch (error) {
      console.warn('Failed to track sign-out event:', error);
    }
    try {
      analytics.reset();
    } catch (error) {
      console.warn('Failed to reset analytics:', error);
    }
  };

  const resetPassword = async (email: string) => {
    // Use your correct redirect URL here.
    const redirectURL = 'http://app.quantitysurveyortools.com/auth/reset-password';
    const { error } = await retryWithBackoff(() =>
      supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectURL })
    );
    if (error) throw error;
    try {
      analytics.capture('password_reset_requested', { email });
    } catch (error) {
      console.warn('Failed to track password reset event:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
